/**
 * The dates the "what comes next" card puts on its steps.
 *
 * Built here rather than in the card so the arithmetic stays out of a component
 * that cannot be loaded by the test runner, and built for the card rather than
 * for a section of its own: a patient reading her page has one question about
 * what comes next, not two, and an earlier attempt at this shipped a second
 * heading beside the first saying the same words.
 *
 * Everything is optional on the way out. No announced pregnancy, no dating, or
 * unreviewed content each produce an empty result, and the card falls back to
 * the wording it has always had.
 */

import type { Locale } from "@src/packages/i18n/locale";
import { formatDate } from "@src/packages/i18n/formatters";
import type { EarlyPregnancyDating } from "../plan/earlyPregnancyCards";
import {
  earlyPregnancyMilestones,
  type EarlyPregnancyMilestone,
} from "./earlyPregnancyMilestones";
import {
  addCalendarDays,
  gestationalWeekWindow,
  windowHasPassed,
  type CalendarWindow,
  type GestationSnapshot,
} from "./gestationalDates";
import {
  mayShowGestationalAge,
  type PregnancyAnnouncement,
} from "./gestationalAgeVisibility";

/**
 * The window a milestone falls in, whichever thing it is timed from.
 *
 * A repeat beta counts from the result she recorded; a scan counts from how far
 * along she is. A milestone whose anchor is missing gets no window and keeps its
 * undated wording rather than appearing with a date built on the wrong thing.
 */
function windowFor(
  milestone: EarlyPregnancyMilestone,
  gestation: GestationSnapshot,
  resultDate: string | null,
): CalendarWindow | null {
  if (milestone.timing.anchor === "gestational-weeks") {
    return gestationalWeekWindow(
      gestation,
      milestone.timing.fromWeek,
      milestone.timing.toWeek,
    );
  }
  if (!resultDate) return null;
  const start = addCalendarDays(resultDate, milestone.timing.fromDay);
  const end = addCalendarDays(resultDate, milestone.timing.toDay);
  return start && end ? { start, end } : null;
}

export function buildEarlyPregnancyDating(params: {
  t: (key: string, options?: { params: Record<string, string> }) => string;
  locale: Locale;
  announcement: PregnancyAnnouncement;
  gestation: GestationSnapshot | null;
  /** Precise weeks and days, when the follow-up dated it rather than she did. */
  preciseAge: { weeks: number; dayOfWeek: number } | null;
  /** The recorded result's date, for milestones counted from it. */
  resultDate: string | null;
  today: string;
}): EarlyPregnancyDating | undefined {
  const { t, locale, announcement, gestation, preciseAge, resultDate } = params;
  if (!mayShowGestationalAge({ announcement, gestation })) return undefined;
  if (!gestation) return undefined;

  const milestones = earlyPregnancyMilestones();
  if (milestones.length === 0) return undefined;

  const NS = "homepage.review_concept.post_procedure_outcome.early_pregnancy";
  const day = { day: "numeric", month: "short" } as const;

  const windows: Record<string, string> = {};
  for (const milestone of milestones) {
    const window = windowFor(milestone, gestation, resultDate);
    if (!window) continue;
    // A window already behind her is not an estimate of something coming. The
    // step keeps its place — it is still part of what usually happens — but
    // without a tag promising a date that has been and gone.
    if (windowHasPassed(window, params.today)) continue;
    windows[milestone.id] = t(`${NS}.usually_between_dates`, {
      params: {
        start: formatDate(window.start, locale, day),
        end: formatDate(window.end, locale, day),
      },
    });
  }
  if (Object.keys(windows).length === 0) return undefined;

  // The precise reading when the follow-up dated it, "about six weeks" when the
  // number came from her in whole weeks. Quoting a day count she never gave
  // would be borrowing precision from nowhere.
  const ageLabel = preciseAge
    ? t(`${NS}.weeks_and_days_along`, {
        params: {
          weeks: String(preciseAge.weeks),
          days: String(preciseAge.dayOfWeek),
        },
      })
    : t(`${NS}.about_weeks_along`, {
        params: { weeks: String(Math.floor(gestation.gestationalDays / 7)) },
      });

  return {
    ageLabel,
    windows,
    indicativeNote: t(`${NS}.your_clinic_plan_comes_first`),
  };
}
